<template>
  <NewBsBox
    ref="containerRef"
    as="figure"
    data
    :bs="{
      'cmp.video': 'container',
      'cmp.video.fullScreen.container': fullscreen,
      ...bs
    }"
  >
    <video
      :id="id"
      ref="videoRef"
      :key="src"
      :data-portrait="videoState.isPortrait || undefined"
      :poster="poster"
      :crossorigin="crossorigin"
      preload="metadata"
      v-bind="attrs"
      controls
      @timeupdate="timeUpdate"
      @loadedmetadata="handleMeta"
      @play="() => changeButtonState('playpause')"
      @pause="() => changeButtonState('playpause')"
      @volumechange="() => checkVolume()"
    >
      <source
        :src="src"
        :type="type"
      >
      <!-- @slot 用来存放更多的 video source 或者 flash callback -->
      <slot />
      <a :href="src">下载视频</a>
    </video>
    <NewBsBox
      :bs="{
        'cmp.video': 'controlsWrapper',
        'cmp.video.fullScreen.controlsWrapper': fullscreen,
      }"
    >
      <NewBsBox :bs="{ 'cmp.video': 'controls'}">
        <NewBsBox :bs="{ 'cmp.video': 'rowCenter'}">
          <Tooltip
            placement="top"
            variant="fill"
            :content="videoState.playpause === 'play' ? '播放' : '暂停'"
          >
            <BeerIcon
              :size="20"
              :bs="{ mr: 24 }"
              :icon="videoState.playpause === 'play' ? 'play-f' : 'pause-f'"
              @click="handlePlayPause"
            />
          </Tooltip>
          <div>
            {{ durationText }}
          </div>
        </NewBsBox>

        <div class="icon-list">
          <Tooltip
            placement="top"
            variant="fill"
            :content="videoState.muted ? '开启' : '静音'"
          >
            <BeerIcon
              :icon="videoState.muted ? 'sound-off-b' : 'sound-on-b'"
              :size="20"
              @click="handleMute"
            />
          </Tooltip>
          <Tooltip
            v-if="allowFull && !fullscreen"
            placement="top"
            variant="fill"
            content="全屏"
          >
            <BeerIcon
              icon="screen-filled"
              :size="20"
              @click="handleFull"
            />
          </Tooltip>
        </div>
      </NewBsBox>
      <NewBsBox
        :bs="{
          'cmp.video': 'progressWrapper',
        }"
        @click="handleProgressClick"
      >
        <NewBsBox
          ref="progressRef"
          as="progress"
          :value="videoState.currentTime"
          min="0"
          :max="videoState.duration"
          :bs="{ 'cmp.video': 'progress' }"
        />
        <NewBsBox
          :bs="{
            'cmp.video': 'progressDot',
            left: `${(videoState.currentTime / videoState.duration) * 100}%`,
          }"
        />
      </NewBsBox>
    </NewBsBox>
    <NewBsBox
      v-if="fullscreen"
      :bs="{ 'cmp.video': 'title' }"
      @click="handleExit"
    >
      <div>
        {{ alt }}
      </div>
      <BeerIcon
        :size="24"
        icon="cancel-m"
      />
    </NewBsBox>

    <BeerIcon
      v-if="handlePrev"
      :size="32"
      :bs="{
        'cmp.video': 'arrow',
        fill: 'white',
        left: fullscreen ? 72 : 24,
        transform: 'translateY(-50%) rotate(180deg)',
      }"
      icon="arrow-right-center-m"
      @click="handlePrev"
    />
    <BeerIcon
      v-if="handleNext"
      :size="32"
      :bs="{
        'cmp.video': 'arrow',
        fill: 'white',
        right: fullscreen ? 72 : 24
      }"
      icon="arrow-right-center-m"
      @click="handleNext"
    />
  </NewBsBox>

  <!-- @ts-ignore -->
  <Teleport
    v-if="showFull && allowFull"
    to="body"
  >
    <BeerVideo
      :allow-full="false"
      :src="src"
      fullscreen
      :poster="poster"
      :type="type"
      :alt="alt"
      :handle-mounted="handleFullMounted"
      :handle-next="handleNext"
      :handle-prev="handlePrev"
      :handel-exit-full="handleExit"
    />
  </Teleport>
</template>

<script lang="ts">
  import {
    defineComponent, onMounted,
    onUpdated,
    ComponentPublicInstance,
    ref,
    reactive,
    computed,
  } from 'vue'
  import NewBsBox from '@xhs/yam-beer/cmp/primitive/NewBsBox'
  import BeerIcon from '@xhs/yam-beer/cmp/common/Icon/Icon.vue'
  import Tooltip from '../../overlayers/Tooltip/Tooltip.vue'
  import {
    getCurrentDurationText,
  } from './utils'
  import initStyle from './style'

  initStyle()

  type VideoState = {
    muted: boolean
    duration: number
    playpause: string
    currentTime: number
    volume: number
    isPortrait: boolean
  }

  export default defineComponent({
    name: 'BeerVideo',
    components: {
      NewBsBox,
      BeerIcon,
      Tooltip,
    },
    props: {
      /**
       * @ignore
       * 底层容器样式
       * @version 4.1.0
       */
      bs: {
        type: Object,
        default: () => ({}),
      },
      /**
       * 封面图
       * @version 4.1.0
       */
      poster: {
        type: String,
        required: false,
      },
      /**
       * 视频地址
       * @version 4.1.0
       */
      src: {
        type: String,
        required: true,
      },
      /**
       * 视频类型
       * @version 4.1.0
       */
      type: {
        type: String,
        default: 'video/mp4',
      },
      /**
       * 视频标题
       * @version 4.1.0
       */
      alt: {
        type: String,
        default: '',
      },
      /**
       * 是否全屏
       * @version 4.1.0
       */
      fullscreen: {
        type: Boolean,
        default: false,
      },
      /**
       * 退出全屏的事件处理
       * @version 4.1.0
       */
      handelExitFull: {
        type: Function,
        required: false,
      },
      /**
       * Video 显示后的 callback
       * @version 4.1.0
       */
      handleMounted: {
        type: Function,
        required: false,
      },
      /**
       * 点击下一个按钮，无值则不显示下一个按钮
       * @version 4.1.0
       */
      handleNext: {
        type: Function,
        required: false,
      },
      /**
       * 点击上一个按钮，无值则不显示上一个按钮
       * @version 4.1.0
       */
      handlePrev: {
        type: Function,
        required: false,
      },
      /**
       * 是否显示全屏的按钮
       * @version 4.1.0
       */
      allowFull: {
        type: Boolean,
        default: true,
      },

      /**
       * <video /> 设置跨域
       * @see MDN-link: https://developer.mozilla.org/zh-CN/docs/Web/HTML/Attributes/crossorigin
       */
      crossorigin: {
        type: String,
        default: null,
      },
      /**
       * 设置在 video 元素上
      */
      id: {
        type: String,
        default: null,
      },
    },
    setup(props, { attrs }) {
      const videoRef = ref<HTMLVideoElement>()
      const progressRef = ref<ComponentPublicInstance>()
      const containerRef = ref<ComponentPublicInstance>()
      const videoState = reactive<VideoState>({
        muted: false,
        duration: 0,
        playpause: 'play',
        currentTime: 0,
        volume: 1,
        isPortrait: false,
      })
      const showFull = ref(false)
      function enSureCustomControl() {
        if (videoRef.value?.controls) {
          videoRef.value.controls = false
        }
      }
      onMounted(() => {
        enSureCustomControl()
        props.handleMounted?.(videoRef.value)
      })
      onUpdated(enSureCustomControl)

      function changeButtonState(type: string) {
        if (!videoRef.value) {
          return
        }
        const video = videoRef.value
        if (type === 'playpause') {
          if (video.paused || video.ended) {
            videoState.playpause = 'play'
          } else {
            videoState.playpause = 'pause'
          }
        } else if (type === 'mute') {
          videoState.muted = video.muted
        }
      }

      function checkVolume(dir: string) {
        if (!videoRef.value) {
          return
        }
        const video = videoRef.value
        if (dir) {
          const currentVolume = Math.floor(video.volume * 10) / 10
          if (dir === '+') {
            if (currentVolume < 1) {
              video.volume += 0.1
            }
          } else if (dir === '-') {
            if (currentVolume < 0) {
              video.volume -= 0.1
            }
          }
          if (currentVolume <= 0) {
            video.muted = true
          } else {
            video.muted = false
          }
          videoState.volume = video.volume
          changeButtonState('mute')
        }
      }

      return {
        attrs,
        videoRef,
        containerRef,
        progressRef,
        videoState,
        showFull,
        durationText: computed(() => getCurrentDurationText(videoState.currentTime, videoState.duration)),
        handleFullMounted(video: any) {
          video.muted = videoState.muted
          video.currentTime = videoState.currentTime
          if (videoState.playpause === 'pause') {
            video.play()
            videoRef.value?.pause()
          }
        },
        handleMeta() {
          if (videoRef.value) {
            videoState.duration = videoRef.value.duration
            videoState.isPortrait = videoRef.value.videoHeight > videoRef.value.videoWidth
          }
        },
        changeButtonState,
        checkVolume,
        handlePlayPause() {
          if (!videoRef.value) {
            return
          }
          const video = videoRef.value
          if (video.paused || video.ended) {
            video.play()
          } else {
            video.pause()
          }
        },
        handleMute() {
          if (!videoRef.value) {
            return
          }
          const video = videoRef.value
          video.muted = !video.muted
          changeButtonState('mute')
        },
        handleVolInc() {
          checkVolume('+')
        },
        handleVolDec() {
          checkVolume('-')
        },
        handleFull() {
          showFull.value = true
        },
        handleExit() {
          showFull.value = false
          props.handelExitFull?.()
        },
        timeUpdate() {
          if (!videoRef.value) {
            return
          }
          const video = videoRef.value
          if (videoState.duration !== video.duration && !Number.isNaN(video.duration)) {
            videoState.duration = video.duration
          }
          videoState.currentTime = video.currentTime
        },
        handleProgressClick(e: any) {
          if (!videoRef.value || !progressRef.value) {
            return
          }
          const video = videoRef.value
          const { x, width } = progressRef.value.$el.getBoundingClientRect()
          const percent = Math.min(1, Math.max(0, (e.pageX - x) / width))
          video.currentTime = video.duration * percent
          videoState.currentTime = video.currentTime
        },
      }
    },
  })
</script>
